import { MAX_PAN_SCALE, MIN_PAN_SCALE } from "./useStagePanZoom";

/** Discrete grid spacings (inches) the canvas can show, ordered from coarsest
 *  to finest. Each step is an integer divisor of the previous one — that's
 *  what makes the crossfade between adjacent levels visually seamless: every
 *  line drawn by the finer grid that is also a line in the coarser grid lands
 *  on the same screen pixel, so as the fine layer reaches full opacity it
 *  becomes a perfect superset of the coarse layer (and we then promote it to
 *  the new coarse layer with no perceptible jump).
 *
 *  The user-facing contract — "1 ft at min zoom, 1 in at max zoom" — comes
 *  from the first/last entries; the intermediate stops keep transitions to a
 *  reasonable cadence (~2.4× zoom per stop given the 0.4–6× pan range). */
const STEPS_IN = [12, 6, 3, 1] as const;

export interface GridStepInfo {
  /** Currently dominant grid spacing in inches. The grid renders this layer
   *  at full minor opacity; the legend reports this value to the user. */
  coarse: number;
  /** Next-finer subdivision in inches. Lines that exist only at this step
   *  (i.e., not shared with the coarse step) fade in as the user zooms toward
   *  the next breakpoint, providing the smooth between-levels transition. */
  fine: number;
  /** Opacity multiplier (0..1) for the fine-only lines. 0 at the start of a
   *  zoom band, 1 at the next breakpoint — at which point `fine` is promoted
   *  to `coarse` and a new `fine` (one step finer) starts fading in. */
  fineOpacity: number;
  /** Always 12. Exposed so callers don't have to import another constant to
   *  identify the always-on "foot" grid lines. */
  major: number;
}

/** Map a Stage scale (the multiplier from `useStagePanZoom`) to the grid step
 *  it should display. Logarithmic interpolation, since zoom is multiplicative:
 *  doubling the zoom should feel like the same "amount of change" no matter
 *  where in the range you are. */
export function gridStepsFor(panScale: number): GridStepInfo {
  const lo = Math.log(MIN_PAN_SCALE);
  const hi = Math.log(MAX_PAN_SCALE);
  const t = Math.max(
    0,
    Math.min(1, (Math.log(panScale) - lo) / (hi - lo))
  );
  // t=0 → STEPS_IN[0] (coarsest, 12"), t=1 → STEPS_IN[last] (finest, 1").
  const idxFloat = t * (STEPS_IN.length - 1);
  const i = Math.floor(idxFloat);
  const j = Math.min(i + 1, STEPS_IN.length - 1);
  return {
    coarse: STEPS_IN[i],
    fine: STEPS_IN[j],
    fineOpacity: i === j ? 0 : idxFloat - i,
    major: 12,
  };
}

/** Formats a grid spacing in inches as a short legend label. Imperial picks
 *  feet (1′) over inches when the value is a whole number of feet so the
 *  legend reads naturally at the coarsest step; metric converts to cm. */
export function formatGridSize(
  inches: number,
  unit: "imperial" | "metric"
): string {
  if (unit === "metric") {
    const cm = Math.round(inches * 2.54);
    return `${cm} cm`;
  }
  if (inches >= 12 && inches % 12 === 0) {
    const ft = inches / 12;
    return `${ft}′`;
  }
  return `${inches}″`;
}
